import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';

import { SnAbilityService } from '@lib/ability';
import { SnAuditOperation, SnContentType } from '@lib/api/entities';
import { SnAuditService } from '@lib/api/services';
import { SnDialogService } from '@lib/ui/dialog';

import { PtzControlsService } from '../../ptz-controls.service';
import { SnVideoPlayerComponent } from '../../video-player.component';

@Component({
  selector: 'sn-ptz-presets-component',
  templateUrl: './ptz-presets.component.html',
  styleUrls: ['./ptz-presets.component.scss'],
})
export class PtzPresetsComponentComponent implements OnInit, OnDestroy {
  get mouseWheel$(): Observable<any> {
    return this._mouseWheel$.asObservable();
  }

  @Input()
  public ptzConfig: any;

  @Input()
  public local: boolean;

  @Input()
  public isExternalPtz: boolean;

  public timer;
  public xPos;
  public yPos;
  public canvas: any;
  public centerX: number;
  public centerY: number;
  public createdLine: any;
  public linesToDraw: any[] = [];
  public presets = [];
  public totalPages = 0;
  public hasResults = 0;
  public page = 0;
  public amount: number = 0;
  public moveMultiplier: number = 5;
  public ptzOverlay: boolean;
  public isSaving: boolean = false;
  public isEditing: boolean = false;
  public loadingPtz: boolean = false;
  public noPresets: boolean = true;
  public isMoving: boolean = false;
  public isOpen: boolean = false;
  public isCrosshairEnabled = false;
  public isDragging: boolean = false;
  public metaPagination: any = {};
  public readonly isMovePTZAllowed: boolean;
  public readonly canChangePresets: boolean;
  public isAreaZoomDrawing: boolean = false;
  public showCrosshair: boolean = false;
  public selectedPreset;
  public form: UntypedFormGroup = new UntypedFormGroup({ name: new UntypedFormControl('') });
  public displayedColumns: string[] = ['presetNumber', 'presetName', 'actions'];

  private readonly _destroy$ = new Subject<void>();
  private readonly _mouseWheel$ = new Subject<any>();

  constructor(
    @Inject('SN_PLAYER') public _player: SnVideoPlayerComponent,
    private readonly _ptzService: PtzControlsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _ability: SnAbilityService,
    public elementRef: ElementRef,
    private readonly _dialogService: SnDialogService,
    private readonly _auditService: SnAuditService,
  ) {
    this.isMovePTZAllowed = this._ability.can({ subject: 'cameras/camera', action: 'move_ptz' });
    this.canChangePresets = this._ability.can({
      subject: 'cameras/camera',
      action: 'change_ptz_presets',
    });
  }

  public ngOnInit(): void {
    if (this.ptzConfig) {
      this._ptzService.setConfig(this.ptzConfig, this.local);
    }

    this._ptzService.openPresetsMenu$.pipe(takeUntil(this._destroy$)).subscribe((event) => {
      if (event) {
        this.openPresetMenu();
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get hasPresets(): any {
    return this.presets;
  }

  get fullscreen(): any {
    return this._player.fullscreen;
  }

  get playerState(): any {
    return this._player.playerState;
  }

  get live(): any {
    return this._player.live;
  }

  public stripAlpha(text: string): string {
    return text?.toString()?.replace(/\D/g, '');
  }

  public savePresetAudit(): void {
    const payload: any = {
      object: {
        id: this.ptzConfig.id,
        name: this.ptzConfig.cameraName,
        contentType: SnContentType.Camera,
      },
      operation: SnAuditOperation.SavePtzPreset,
      detail: {
        name: this.selectedPreset.presetName,
      },
    };
    this._auditService
      .createAudit([payload])
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  public savePreset(edit?: boolean): void {
    this.isSaving = true;
    this.selectedPreset.presetName = this.form.value.name;
    const request$ =
      !this.ptzConfig.isAxis && !this.ptzConfig.isSony && !this.ptzConfig.isAvigilon
        ? this._ptzService.editPreset(
            this.selectedPreset.presetNumber,
            this.selectedPreset.presetName,
          )
        : this.ptzConfig.isAvigilon
        ? this._ptzService.createPreset(
            this.selectedPreset.presetName,
            this.metaPagination.totalCount,
          )
        : this._ptzService.createPreset(this.selectedPreset.presetName);
    request$.pipe(takeUntil(this._destroy$)).subscribe({
      next: (res) => {
        this._handleResponse(res);
        // eslint-disable-next-line no-unsafe-optional-chaining
        const { results } = res?.result?.presets || res?.data;
        if (results?.length !== 0) {
          this.totalPages = res?.result?.presets?.totalPages
            ? res?.result?.presets?.totalPages - 1
            : res?.data?.totalPages
            ? res?.data?.totalPages - 1
            : 0;
          this.page = res?.result?.presets?.totalPages
            ? res?.result?.presets?.totalPages - 1
            : res?.data?.totalPages
            ? res?.data?.totalPages - 1
            : 0;
          this._changeDetectorRef.detectChanges();
        } else {
          this.page = 0;
          this._changeDetectorRef.detectChanges();
        }

        this.savePresetAudit();
        this.isSaving = false;
        this.isEditing = false;
        this.updateList(true);
      },
      error: () => {
        this.isSaving = false;
        this.isEditing = false;
        this.updateList();
      },
    });
  }

  public updateList(goLatPage: boolean = false): void {
    this.loadingPtz = true;
    const page = goLatPage ? this.totalPages : this.page;
    this._ptzService
      .getPtzList(page)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this.noPresets = this.ptzConfig.isExternalPtz
            ? !response?.data?.results?.length
            : !response?.presets?.results?.length;
          this._handleResponse(response);
          this.loadingPtz = false;
          this._changeDetectorRef.detectChanges();
        },
        error: () => {
          this.noPresets = true;
          this.isMoving = false;
          this.loadingPtz = false;
          this.isSaving = false;
        },
      });
  }

  public allowPTZ(): boolean {
    return (
      this._player.fullscreen &&
      this.ptzConfig?.ptzMoveUrls &&
      this._player.playerState === 'playing' &&
      this.isMovePTZAllowed
    );
  }

  public close(): void {
    this._ptzService.openPresetsMenu();
  }

  public goToPreset(preset: any): void {
    this.isMoving = true;
    this._ptzService
      .goToPreset(preset.presetNumber)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.isMoving = false;
          this._changeDetectorRef.detectChanges();
        },
        error: () => {
          this.isMoving = false;
          this._changeDetectorRef.detectChanges();
        },
      });
  }

  public cancelEdit(): void {
    this.isEditing = false;
    this.updateList();
  }

  public editPreset(preset: any): void {
    this.isEditing = true;
    this.form.patchValue({ name: preset.presetName });
    this.selectedPreset = preset;
  }

  public deletePresetAudit(): void {
    const payload: any = {
      object: {
        id: this.ptzConfig.id,
        name: this.ptzConfig.cameraName,
        contentType: SnContentType.Camera,
      },
      operation: SnAuditOperation.DeletePtzPreset,
      detail: {
        name: this.selectedPreset.presetName,
      },
    };
    this._auditService
      .createAudit([payload])
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  public deletePreset(preset: any): void {
    this._ptzService
      .deletePreset(preset.presetNumber)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (this.hasResults === 0) {
          this.page = 0;
          this._changeDetectorRef.detectChanges();
        }
        this.deletePresetAudit();
        this.updateList();
      });
  }

  public delete(preset: any): void {
    const message = 'Are you sure you want to delete this preset?';

    this._dialogService
      .confirm({
        title: 'Delete',
        message,
        confirmButton: {
          text: 'CONFIRM',
          color: 'warn',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((c) => {
        this.deletePreset(preset);
      });
  }

  public openPresetMenu(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.updateList();
    } else {
      this.page = 0;
    }
    this._changeDetectorRef.detectChanges();
  }

  public onPagination(event: PageEvent): void {
    this.page = event.pageIndex;
    this.updateList();
  }

  private _handleResponse(res: any): void {
    this.metaPagination.totalCount = this.ptzConfig.isExternalPtz
      ? res.data.totalCount
      : res.presets?.totalCount || 0;
    this.metaPagination.itemPerPages = 5;
    this.totalPages = this.ptzConfig.isExternalPtz
      ? res.data?.totalPages || 0
      : res.presets?.totalPages || 0;
    this.hasResults = this.ptzConfig.isExternalPtz
      ? res?.data?.results?.length
      : res?.presets?.results?.length;
    this.presets = this.ptzConfig.isExternalPtz ? res?.data?.results : res?.presets?.results;
  }
}
